/**
 * Inventory Page
 * Vista para gestionar el inventario de materiales y equipamiento
 */

import { useState, useMemo, useEffect } from "react";
import { AlertModal } from "@/components/ui/AlertModal/AlertModal";
import { materialsService } from "@/services/materialsService";
import { historicalStockService } from "@/services/historicalStockService";
import type { MaterialCategory, MaterialUnit } from "@/types";
import styles from "./InventoryPage.module.css";

interface Material {
  id: number;
  name: string;
  category: string;
  quantity: number;
  unit: string;
  minStock: number;
  price: number;
  supplier: string;
  location: string;
  expiryDate: string | null;
  lastUpdated: string;
}

interface Movement {
  id: number;
  materialId: number;
  materialName: string;
  type: "addition" | "removal";
  quantity: number;
  reason: string;
  user: string;
  date: string;
}

type StockStatus = "ok" | "low" | "out";
type ViewMode = "materials" | "movements";

// Mapping helpers: API category values <-> UI display strings
const API_TO_UI_CATEGORY: Record<string, string> = {
  insecticides: "Insecticidas",
  rodenticides: "Rodenticidas",
  traps: "Trampas",
  equipment: "Equipamiento",
  disinfectants: "Desinfectantes",
};

const UI_TO_API_CATEGORY: Record<string, MaterialCategory> = {
  Insecticidas: "insecticides",
  Rodenticidas: "rodenticides",
  Trampas: "traps",
  Equipamiento: "equipment",
  Desinfectantes: "disinfectants",
};

// Mapping helpers: API unit values <-> UI display strings
const API_TO_UI_UNIT: Record<string, string> = {
  kg: "kg",
  l: "litros",
  unit: "unidades",
  boxes: "cajas",
  pipes: "tubos",
};

const UI_TO_API_UNIT: Record<string, MaterialUnit> = {
  kg: "kg",
  litros: "l",
  unidades: "unit",
  cajas: "boxes",
  tubos: "pipes",
};

const InventoryPage = () => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);

  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(
    null,
  );
  const [showModal, setShowModal] = useState(false);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "error" | "success";
  }>({
    isOpen: false,
    message: "",
    type: "info",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");
  const [viewMode, setViewMode] = useState<ViewMode>("materials");

  const categories = [
    "Insecticidas",
    "Rodenticidas",
    "Trampas",
    "Equipamiento",
    "Desinfectantes",
  ];

  const [formData, setFormData] = useState({
    name: "",
    category: "Insecticidas",
    quantity: "",
    unit: "litros",
    minStock: "",
    price: "",
    supplier: "",
    location: "",
    expiryDate: "",
  });

  const [movementForm, setMovementForm] = useState({
    materialId: "",
    type: "addition" as "addition" | "removal",
    quantity: "",
    reason: "",
  });

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const [materialsRes, movementsRes] = await Promise.all([
          materialsService.list({ page_size: 100 }),
          historicalStockService.list({ page_size: 100 }),
        ]);

        const mappedMaterials: Material[] = materialsRes.results.map((m) => ({
          id: m.id,
          name: m.name,
          category: API_TO_UI_CATEGORY[m.category] ?? m.category,
          quantity: m.quantity ?? 0,
          unit: API_TO_UI_UNIT[m.unit ?? ""] ?? m.unit ?? "unidades",
          minStock: m.minimum_stock ?? 0,
          price: parseFloat(m.price_per_unit ?? "0"),
          supplier: m.provider ?? "",
          location: m.location ?? "",
          expiryDate: m.caducity_date ?? null,
          lastUpdated: m.updated_at.split("T")[0],
        }));

        const mappedMovements: Movement[] = movementsRes.results.map((hs) => ({
          id: hs.id,
          materialId: hs.material,
          materialName: hs.material_name,
          type: (hs.change_type === "addition" ? "addition" : "removal") as
            | "addition"
            | "removal",
          quantity: hs.quantity_changed,
          reason: hs.reason ?? "",
          user: hs.user ? `Usuario #${hs.user}` : "Sistema",
          date: hs.date,
        }));

        setMaterials(mappedMaterials);
        setMovements(mappedMovements);
      } catch (err) {
        setApiError("Error al cargar el inventario");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, []);

  const filteredMaterials = useMemo(() => {
    return materials.filter((material) => {
      const matchesSearch =
        material.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        material.supplier.toLowerCase().includes(searchTerm.toLowerCase()) ||
        material.location.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        categoryFilter === "all" || material.category === categoryFilter;

      let matchesStock = true;
      if (stockFilter === "low") {
        matchesStock = material.quantity <= material.minStock;
      } else if (stockFilter === "ok") {
        matchesStock = material.quantity > material.minStock;
      }

      return matchesSearch && matchesCategory && matchesStock;
    });
  }, [materials, searchTerm, categoryFilter, stockFilter]);

  const lowStockCount = materials.filter(
    (m) => m.quantity <= m.minStock,
  ).length;
  const totalValue = materials.reduce(
    (sum, m) => sum + m.quantity * m.price,
    0,
  );

  const handleOpenModal = (material: Material | null = null) => {
    if (material) {
      setFormData({
        name: material.name,
        category: material.category,
        quantity: material.quantity.toString(),
        unit: material.unit,
        minStock: material.minStock.toString(),
        price: material.price.toString(),
        supplier: material.supplier,
        location: material.location,
        expiryDate: material.expiryDate || "",
      });
      setSelectedMaterial(material);
    } else {
      setFormData({
        name: "",
        category: "Insecticidas",
        quantity: "",
        unit: "litros",
        minStock: "",
        price: "",
        supplier: "",
        location: "",
        expiryDate: "",
      });
      setSelectedMaterial(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedMaterial(null);
  };

  const handleSaveMaterial = async () => {
    if (
      !formData.name ||
      !formData.quantity ||
      !formData.minStock ||
      !formData.price
    ) {
      setAlertModal({
        isOpen: true,
        message: "Por favor, completa todos los campos obligatorios",
        type: "warning",
      });
      return;
    }

    const apiData = {
      name: formData.name,
      category:
        UI_TO_API_CATEGORY[formData.category] ??
        ("insecticides" as MaterialCategory),
      unit: UI_TO_API_UNIT[formData.unit] ?? ("unit" as MaterialUnit),
      quantity: parseFloat(formData.quantity),
      minimum_stock: parseFloat(formData.minStock),
      price_per_unit: formData.price,
      provider: formData.supplier || null,
      location: formData.location || null,
      caducity_date: formData.expiryDate || null,
      active: true,
    };

    try {
      if (selectedMaterial) {
        await materialsService.patch(selectedMaterial.id, apiData);
        const localData = {
          name: formData.name,
          category: formData.category,
          quantity: parseFloat(formData.quantity),
          unit: formData.unit,
          minStock: parseFloat(formData.minStock),
          price: parseFloat(formData.price),
          supplier: formData.supplier,
          location: formData.location,
          expiryDate: formData.expiryDate || null,
          lastUpdated: new Date().toISOString().split("T")[0],
        };
        setMaterials(
          materials.map((m) =>
            m.id === selectedMaterial.id ? { ...m, ...localData } : m,
          ),
        );
      } else {
        const created = await materialsService.create(apiData);
        const newMaterial: Material = {
          id: created.id,
          name: created.name,
          category: API_TO_UI_CATEGORY[created.category] ?? created.category,
          quantity: created.quantity ?? 0,
          unit:
            API_TO_UI_UNIT[created.unit ?? ""] ?? created.unit ?? "unidades",
          minStock: created.minimum_stock ?? 0,
          price: parseFloat(created.price_per_unit ?? "0"),
          supplier: created.provider ?? "",
          location: created.location ?? "",
          expiryDate: created.caducity_date ?? null,
          lastUpdated: created.updated_at.split("T")[0],
        };
        setMaterials([...materials, newMaterial]);
      }
    } catch (err) {
      console.error("Error guardando material:", err);
      setAlertModal({
        isOpen: true,
        message: "Error al guardar el material",
        type: "error",
      });
      return;
    }

    handleCloseModal();
  };

  const handleDeleteMaterial = async (id: number) => {
    if (confirm("¿Estás seguro de que quieres eliminar este material?")) {
      try {
        await materialsService.remove(id);
        setMaterials(materials.filter((m) => m.id !== id));
        if (selectedMaterial?.id === id) {
          setSelectedMaterial(null);
        }
      } catch (err) {
        console.error("Error eliminando material:", err);
        setAlertModal({
          isOpen: true,
          message: "Error al eliminar el material",
          type: "error",
        });
      }
    }
  };

  const handleOpenMovementModal = () => {
    setMovementForm({
      materialId: materials[0]?.id.toString() || "",
      type: "addition",
      quantity: "",
      reason: "",
    });
    setShowMovementModal(true);
  };

  const handleSaveMovement = async () => {
    if (
      !movementForm.materialId ||
      !movementForm.quantity ||
      !movementForm.reason
    ) {
      setAlertModal({
        isOpen: true,
        message: "Por favor, completa todos los campos",
        type: "warning",
      });
      return;
    }

    const material = materials.find(
      (m) => m.id === parseInt(movementForm.materialId),
    );
    if (!material) return;

    const quantity = parseFloat(movementForm.quantity);

    if (movementForm.type === "removal" && material.quantity < quantity) {
      setAlertModal({
        isOpen: true,
        message: "No hay suficiente stock para esta salida",
        type: "error",
      });
      return;
    }

    try {
      const created = await historicalStockService.create({
        material: parseInt(movementForm.materialId),
        change_type: movementForm.type,
        quantity_changed: quantity,
        reason: movementForm.reason,
        date: new Date().toISOString(),
      });

      // Actualizar stock del material
      setMaterials(
        materials.map((m) =>
          m.id === parseInt(movementForm.materialId)
            ? {
                ...m,
                quantity:
                  movementForm.type === "addition"
                    ? m.quantity + quantity
                    : m.quantity - quantity,
                lastUpdated: new Date().toISOString().split("T")[0],
              }
            : m,
        ),
      );

      // Registrar movimiento con datos devueltos por la API
      const newMovement: Movement = {
        id: created.id,
        materialId: created.material,
        materialName: created.material_name,
        type: (created.change_type === "addition" ? "addition" : "removal") as
          | "addition"
          | "removal",
        quantity: created.quantity_changed,
        reason: created.reason ?? movementForm.reason,
        user: "Usuario Actual",
        date: created.date,
      };

      setMovements([newMovement, ...movements]);
    } catch (err) {
      console.error("Error registrando movimiento:", err);
      setAlertModal({
        isOpen: true,
        message: "Error al registrar el movimiento",
        type: "error",
      });
      return;
    }

    setShowMovementModal(false);
  };

  const getStockStatus = (material: Material): StockStatus => {
    if (material.quantity === 0) return "out";
    if (material.quantity <= material.minStock) return "low";
    return "ok";
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading)
    return (
      <div className={styles.container}>
        <p style={{ padding: "2rem" }}>Cargando inventario...</p>
      </div>
    );
  if (apiError)
    return (
      <div className={styles.container}>
        <p style={{ padding: "2rem", color: "red" }}>{apiError}</p>
      </div>
    );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1>📦 Inventario</h1>
          <div className={styles.stats}>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Total Materiales</span>
              <span className={styles.statValue}>{materials.length}</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Stock Bajo</span>
              <span
                className={`${styles.statValue} ${lowStockCount > 0 ? styles.warning : ""}`}
              >
                {lowStockCount}
              </span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Valor Total</span>
              <span className={styles.statValue}>{totalValue.toFixed(2)}€</span>
            </div>
          </div>
        </div>
        <button className={styles.addButton} onClick={() => handleOpenModal()}>
          + Nuevo Material
        </button>
      </div>

      <div className={styles.viewToggle}>
        <button
          className={`${styles.toggleButton} ${viewMode === "materials" ? styles.active : ""}`}
          onClick={() => setViewMode("materials")}
        >
          📦 Materiales
        </button>
        <button
          className={`${styles.toggleButton} ${viewMode === "movements" ? styles.active : ""}`}
          onClick={() => setViewMode("movements")}
        >
          📋 Movimientos
        </button>
      </div>

      {viewMode === "materials" ? (
        <>
          <div className={styles.controls}>
            <div className={styles.searchBar}>
              <input
                type="text"
                placeholder="🔍 Buscar material, proveedor o ubicación..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles.filters}>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">Todas las categorías</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              <select
                value={stockFilter}
                onChange={(e) => setStockFilter(e.target.value)}
              >
                <option value="all">Todo el stock</option>
                <option value="low">Stock bajo</option>
                <option value="ok">Stock normal</option>
              </select>
            </div>
          </div>

          <div className={styles.materialsGrid}>
            {filteredMaterials.map((material) => {
              const stockStatus = getStockStatus(material);
              return (
                <div
                  key={material.id}
                  className={`${styles.materialCard} ${styles[stockStatus]}`}
                >
                  <div className={styles.cardHeader}>
                    <h3>{material.name}</h3>
                    <span className={styles.category}>{material.category}</span>
                  </div>
                  <div className={styles.stockInfo}>
                    <div className={styles.stockAmount}>
                      <span className={styles.quantity}>
                        {material.quantity} {material.unit}
                      </span>
                      {stockStatus === "low" && (
                        <span className={styles.lowStockBadge}>
                          ⚠️ Stock bajo
                        </span>
                      )}
                      {stockStatus === "out" && (
                        <span className={styles.outStockBadge}>
                          ❌ Sin stock
                        </span>
                      )}
                    </div>
                    <div className={styles.minStock}>
                      Mínimo: {material.minStock} {material.unit}
                    </div>
                  </div>
                  <div className={styles.cardDetails}>
                    <div className={styles.detailRow}>
                      <span>💰 Precio:</span>
                      <strong>
                        {material.price.toFixed(2)}€/{material.unit}
                      </strong>
                    </div>
                    <div className={styles.detailRow}>
                      <span>🏢 Proveedor:</span>
                      <span>{material.supplier}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span>📍 Ubicación:</span>
                      <span>{material.location}</span>
                    </div>
                    {material.expiryDate && (
                      <div className={styles.detailRow}>
                        <span>📅 Caducidad:</span>
                        <span>{formatDate(material.expiryDate)}</span>
                      </div>
                    )}
                    <div className={styles.detailRow}>
                      <span>🔄 Actualizado:</span>
                      <span>{formatDate(material.lastUpdated)}</span>
                    </div>
                  </div>
                  <div className={styles.cardActions}>
                    <button
                      className={styles.editButton}
                      onClick={() => handleOpenModal(material)}
                    >
                      ✏️ Editar
                    </button>
                    <button
                      className={styles.deleteButton}
                      onClick={() => {
                        void handleDeleteMaterial(material.id);
                      }}
                    >
                      🗑️ Eliminar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className={styles.movementsSection}>
          <div className={styles.movementsHeader}>
            <h2>Historial de Movimientos</h2>
            <button
              className={styles.addButton}
              onClick={handleOpenMovementModal}
            >
              + Registrar Movimiento
            </button>
          </div>
          <div className={styles.movementsTable}>
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Material</th>
                  <th>Tipo</th>
                  <th>Cantidad</th>
                  <th>Motivo</th>
                  <th>Usuario</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((movement) => (
                  <tr key={movement.id}>
                    <td>{formatDateTime(movement.date)}</td>
                    <td>{movement.materialName}</td>
                    <td>
                      <span
                        className={`${styles.movementType} ${styles[movement.type]}`}
                      >
                        {movement.type === "addition"
                          ? "⬇️ Entrada"
                          : "⬆️ Salida"}
                      </span>
                    </td>
                    <td
                      className={
                        movement.type === "addition"
                          ? styles.positive
                          : styles.negative
                      }
                    >
                      {movement.type === "addition" ? "+" : "-"}
                      {movement.quantity}
                    </td>
                    <td>{movement.reason}</td>
                    <td>{movement.user}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className={styles.modal} onClick={handleCloseModal}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>
                {selectedMaterial ? "✏️ Editar Material" : "➕ Nuevo Material"}
              </h2>
              <button className={styles.closeButton} onClick={handleCloseModal}>
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Nombre *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Ej: Insecticida Premium"
                />
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Categoría *</label>
                  <select
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Unidad *</label>
                  <select
                    value={formData.unit}
                    onChange={(e) =>
                      setFormData({ ...formData, unit: e.target.value })
                    }
                  >
                    <option value="litros">Litros</option>
                    <option value="kg">Kilogramos</option>
                    <option value="unidades">Unidades</option>
                    <option value="cajas">Cajas</option>
                    <option value="tubos">Tubos</option>
                  </select>
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Cantidad Actual *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.quantity}
                    onChange={(e) =>
                      setFormData({ ...formData, quantity: e.target.value })
                    }
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Stock Mínimo *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.minStock}
                    onChange={(e) =>
                      setFormData({ ...formData, minStock: e.target.value })
                    }
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Precio/Unidad *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) =>
                      setFormData({ ...formData, price: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>Proveedor</label>
                <input
                  type="text"
                  value={formData.supplier}
                  onChange={(e) =>
                    setFormData({ ...formData, supplier: e.target.value })
                  }
                  placeholder="Ej: QuímicaPro S.L."
                />
              </div>
              <div className={styles.formGroup}>
                <label>Ubicación en Almacén</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) =>
                    setFormData({ ...formData, location: e.target.value })
                  }
                  placeholder="Ej: Almacén A - Estante 3"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Fecha de Caducidad</label>
                <input
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) =>
                    setFormData({ ...formData, expiryDate: e.target.value })
                  }
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.cancelButton}
                onClick={handleCloseModal}
              >
                Cancelar
              </button>
              <button
                className={styles.saveButton}
                onClick={() => {
                  void handleSaveMaterial();
                }}
              >
                {selectedMaterial ? "Guardar Cambios" : "Crear Material"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showMovementModal && (
        <div
          className={styles.modal}
          onClick={() => setShowMovementModal(false)}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>📋 Registrar Movimiento</h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowMovementModal(false)}
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Material *</label>
                <select
                  value={movementForm.materialId}
                  onChange={(e) =>
                    setMovementForm({
                      ...movementForm,
                      materialId: e.target.value,
                    })
                  }
                >
                  {materials.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} (Stock: {m.quantity} {m.unit})
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>Tipo de Movimiento *</label>
                <select
                  value={movementForm.type}
                  onChange={(e) =>
                    setMovementForm({
                      ...movementForm,
                      type: e.target.value as "addition" | "removal",
                    })
                  }
                >
                  <option value="addition">⬇️ Entrada (añadir stock)</option>
                  <option value="removal">⬆️ Salida (restar stock)</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>Cantidad *</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={movementForm.quantity}
                  onChange={(e) =>
                    setMovementForm({
                      ...movementForm,
                      quantity: e.target.value,
                    })
                  }
                  placeholder="Cantidad a añadir o restar"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Motivo *</label>
                <input
                  type="text"
                  value={movementForm.reason}
                  onChange={(e) =>
                    setMovementForm({ ...movementForm, reason: e.target.value })
                  }
                  placeholder="Ej: Compra - Factura #1234 o Orden de trabajo OT-000123"
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.cancelButton}
                onClick={() => setShowMovementModal(false)}
              >
                Cancelar
              </button>
              <button
                className={styles.saveButton}
                onClick={() => {
                  void handleSaveMovement();
                }}
              >
                Registrar Movimiento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AlertModal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        message={alertModal.message}
        type={alertModal.type}
      />
    </div>
  );
};

export default InventoryPage;
